import createClient, { type Middleware } from 'openapi-fetch';

import { depuisReponse, erreurReseau, ErreurApi } from './erreurs';
import type { paths } from './genere';
import { lireJetonAcces, obtenirJetonFrais } from './jetons';

/**
 * Client HTTP de l'API Astrolabe.
 *
 * TYPAGE DE BOUT EN BOUT (§0.2) : `paths` est genere depuis l'OpenAPI `/v1`. Les
 * chemins, les corps de requete et les reponses sont donc verifies a la
 * compilation. Un endpoint renomme cote back casse le build du front — ce qui est
 * exactement le comportement voulu pour un contrat partage.
 *
 * L'ENTREPRISE N'EST PAS UN EN-TETE. L'API la resout depuis le jeton, jamais d'un
 * parametre client (§5 du back). Changer d'entreprise consiste donc a echanger le
 * jeton, pas a modifier une requete : c'est le travail de `shared/tenant`.
 * Le developpeur de feature n'a rien a faire — ni jeton, ni entreprise.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

/** En-tete d'authentification, ajoute a chaque requete si une session est ouverte. */
const authentification: Middleware = {
  async onRequest({ request }) {
    const jeton = lireJetonAcces();

    if (jeton) {
      request.headers.set('Authorization', `Bearer ${jeton}`);
    }

    return request;
  },
};

/**
 * Rejoue UNE FOIS une requete refusee en 401, apres rafraichissement du jeton.
 *
 * Le drapeau `x-astrolabe-rejeu` empeche la boucle infinie : si la requete rejouee
 * echoue encore en 401, le rafraichissement n'a servi a rien et il faut laisser
 * l'erreur remonter pour deconnecter proprement.
 */
const rejeuApresExpiration: Middleware = {
  async onResponse({ request, response }) {
    if (response.status !== 401 || request.headers.has('x-astrolabe-rejeu')) {
      return response;
    }

    const jetonFrais = await obtenirJetonFrais();

    if (!jetonFrais) {
      return response;
    }

    const rejeu = new Request(request.clone(), {
      headers: new Headers(request.headers),
    });

    rejeu.headers.set('Authorization', `Bearer ${jetonFrais}`);
    rejeu.headers.set('x-astrolabe-rejeu', '1');

    return fetch(rejeu);
  },
};

export const clientApi = createClient<paths>({ baseUrl: BASE_URL });

clientApi.use(authentification);
clientApi.use(rejeuApresExpiration);

/**
 * Deballe une reponse `openapi-fetch` : renvoie la donnee, ou leve une `ErreurApi`.
 *
 * Les hooks TanStack Query passent tous par ici. Leur `onError` recoit donc
 * toujours une `ErreurApi` normalisee, jamais un objet de forme inconnue —
 * c'est ce qui permet aux composants d'etat de rester simples.
 */
export async function deballer<T>(
  appel: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let resultat: { data?: T; error?: unknown; response: Response };

  try {
    resultat = await appel;
  } catch (cause) {
    throw erreurReseau(cause);
  }

  if (resultat.error !== undefined || !resultat.response.ok) {
    throw depuisReponse(resultat.response.status, resultat.error);
  }

  // 204 No Content : succes sans corps. Le type attendu est alors `void`.
  return resultat.data as T;
}

/**
 * Requete brute, pour les rares cas hors du client type : telechargement d'un
 * PDF archive, envoi d'un fichier. Elle applique la meme authentification.
 */
export async function requeteBrute(
  chemin: string,
  init?: RequestInit,
): Promise<Response> {
  const jeton = lireJetonAcces();
  const entetes = new Headers(init?.headers);

  if (jeton) {
    entetes.set('Authorization', `Bearer ${jeton}`);
  }

  let reponse: Response;

  try {
    reponse = await fetch(`${BASE_URL}${chemin}`, { ...init, headers: entetes });
  } catch (cause) {
    throw erreurReseau(cause);
  }

  if (!reponse.ok) {
    let corps: unknown = null;

    try {
      corps = await reponse.json();
    } catch {
      /* Reponse sans corps JSON exploitable. */
    }

    throw depuisReponse(reponse.status, corps);
  }

  return reponse;
}

export { ErreurApi };
